import { logger } from "@/utils/monitoring/logging"

import { dexie } from "../../db"
import { logDexieError } from "../../utils/logDexieError"

/**
 * Local mirror of a claim damage marker — a freehand line drawn on a claim photo.
 * Maps to the `session_claim_marker` table (points + width).
 */
export type ClaimMarkerLocal = {
  dexieId: number
  id: string
  sessionId: string
  /** FK -> photos.id of the claim main photo the marker is drawn on */
  photoId: string
  /** Freehand line points as [x, y, x, y, ...] in normalized (0-1) image coordinates */
  points: number[]
  /** Stroke width of the drawn line */
  width: number
  createdAt: Date
  deleted?: boolean
  /** Set once the marker has been synced to the backend */
  uploadedAt?: Date
  uploadFailed?: boolean
  /** Set when a server delete-sync attempt failed */
  deleteFailed?: boolean
}

export type AddClaimMarkerInput = Omit<ClaimMarkerLocal, "dexieId" | "createdAt" | "deleted">

export async function addClaimMarker(input: AddClaimMarkerInput): Promise<void> {
  try {
    await dexie.claimMarkers.add({ ...input, createdAt: new Date() } as ClaimMarkerLocal)
  } catch (error) {
    logDexieError(error)
  }
}

export async function getClaimMarkersByPhoto(photoId: string): Promise<ClaimMarkerLocal[]> {
  try {
    const markers = await dexie.claimMarkers.where("photoId").equals(photoId).toArray()
    return markers.filter((marker) => !marker.deleted)
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function findClaimMarkerById(id: string): Promise<ClaimMarkerLocal | undefined> {
  try {
    return await dexie.claimMarkers.where("id").equals(id).first()
  } catch (error) {
    logDexieError(error)
  }
}

export async function getClaimMarkersBySession(sessionId: string, includeDeleted = false): Promise<ClaimMarkerLocal[]> {
  try {
    const markers = await dexie.claimMarkers.where("sessionId").equals(sessionId).toArray()
    return includeDeleted ? markers : markers.filter((marker) => !marker.deleted)
  } catch (error) {
    logDexieError(error)
    return []
  }
}

/** Markers not yet uploaded (not synced, not deleted, not failed) — drives the background uploader */
export async function getPendingClaimMarkersForUpload(sessionId: string): Promise<ClaimMarkerLocal[]> {
  try {
    return await dexie.claimMarkers
      .where("sessionId")
      .equals(sessionId)
      .and((marker) => !marker.uploadedAt && !marker.deleted && !marker.uploadFailed)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function markClaimMarkerUploaded(id: string): Promise<void> {
  try {
    await dexie.claimMarkers.where("id").equals(id).modify({ uploadedAt: new Date() })
  } catch (error) {
    logDexieError(error)
  }
}

export async function markClaimMarkerUploadFailed(id: string): Promise<void> {
  try {
    await dexie.claimMarkers.where("id").equals(id).modify({ uploadFailed: true })
  } catch (error) {
    logDexieError(error)
  }
}

export async function softDeleteClaimMarker(id: string): Promise<void> {
  try {
    logger.info(`[claim] Soft-deleting claim marker ${id} locally`)
    await dexie.claimMarkers.where("id").equals(id).modify({ deleted: true })
  } catch (error) {
    logDexieError(error)
  }
}

export async function hardDeleteClaimMarker(id: string): Promise<void> {
  try {
    await dexie.claimMarkers.where("id").equals(id).delete()
  } catch (error) {
    logDexieError(error)
  }
}

/** Uploaded markers deleted locally that still need their deletion synced to the server */
export async function getPendingClaimMarkerDeletions(sessionId: string): Promise<ClaimMarkerLocal[]> {
  try {
    return await dexie.claimMarkers
      .where("sessionId")
      .equals(sessionId)
      .and((marker) => marker.deleted === true && !!marker.uploadedAt && !marker.deleteFailed)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function markClaimMarkerDeleteFailed(id: string): Promise<void> {
  try {
    await dexie.claimMarkers.where("id").equals(id).modify({ deleteFailed: true })
  } catch (error) {
    logDexieError(error)
  }
}
